import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../table';
import { AdminShadowJob } from '../../stores/adminStore';
import { getJobPublicationLabel } from '../../utils/jobUtils';

interface JobTableProps {
  jobs: AdminShadowJob[];
  onRowClick: (job: AdminShadowJob) => void;
  showAdminActivityHubLink?: boolean;
  showShadowRecruiterColumn?: boolean;
}

const JobTable: React.FC<JobTableProps> = ({
  jobs,
  onRowClick,
  showAdminActivityHubLink,
  showShadowRecruiterColumn,
}) => {
  const { t, i18n } = useTranslation(['admin', 'common', 'jobs']);

  const headerFields = showAdminActivityHubLink
    ? (['title', 'city', 'type', 'status', 'published_at'] as const)
    : (['title', 'city', 'type', 'status'] as const);

  const getShadowRecruiterSummary = (job: AdminShadowJob) => {
    const companyName = job.shadow_recruiter_company_name?.trim() || null;
    const hasShadowAssignment =
      Boolean(job.shadow_recruiter_id) || Boolean(job.is_shadow_recruiter) || Boolean(companyName);

    if (!hasShadowAssignment) {
      return null;
    }

    return {
      primary: companyName || '-',
      secondary: null as string | null,
    };
  };

  return (
    <div className="max-w-full overflow-x-auto">
      <Table className="w-full">
        <TableHeader className="bg-gray-50 dark:bg-white/[0.05]">
          <TableRow>
            {headerFields.map(field => (
              <TableCell
                key={field}
                isHeader
                className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide dark:text-gray-400"
              >
                {t(`table_fields.${field}`, { ns: 'admin' })}
              </TableCell>
            ))}
            {showShadowRecruiterColumn ? (
              <TableCell
                isHeader
                className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide dark:text-gray-400"
              >
                {t('shadowRecruiters.column', {
                  ns: 'admin',
                  defaultValue: 'Shadow recruiter',
                })}
              </TableCell>
            ) : null}
            {showAdminActivityHubLink ? (
              <TableCell
                isHeader
                className="text-start px-6 py-4 font-semibold uppercase text-gray-600 text-[0.75rem] tracking-wide dark:text-gray-400"
              >
                {t('jobActivityHub.actions_column', { ns: 'admin', defaultValue: 'Hub' })}
              </TableCell>
            ) : null}
          </TableRow>
        </TableHeader>

        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
          {jobs.map(job => {
            const shadowRecruiterSummary = getShadowRecruiterSummary(job);

            return (
              <TableRow
                key={job.id}
                onClick={() => onRowClick(job)}
                className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-white/[0.08]"
              >
                <TableCell className="px-6 py-4">{job.title}</TableCell>

                <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                  {job.city?.name || '-'}
                </TableCell>

                <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                  <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300">
                    {t(`job_visibility_labels.${job.visibility}`, { ns: 'jobs' })}
                  </span>
                </TableCell>

                <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                  <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-white/[0.08] dark:text-gray-300">
                    {t(`job_statuses.${job.status}`, { ns: 'common' })}
                  </span>
                </TableCell>

                {showAdminActivityHubLink ? (
                  <TableCell className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                    {getJobPublicationLabel(job, t, i18n.language)}
                  </TableCell>
                ) : null}

                {showShadowRecruiterColumn ? (
                  <TableCell className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {shadowRecruiterSummary ? (
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {shadowRecruiterSummary.primary}
                        </span>
                        {shadowRecruiterSummary.secondary ? (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {shadowRecruiterSummary.secondary}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                ) : null}

                {showAdminActivityHubLink ? (
                  <TableCell className="px-6 py-4">
                    <Link
                      to={`/admin/jobs/${job.id}/hub`}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs font-medium text-gray-800 hover:bg-gray-50 dark:border-white/[0.1] dark:text-gray-100 dark:hover:bg-white/[0.06]"
                      onClick={e => e.stopPropagation()}
                      title={t('jobActivityHub.open_hub', { ns: 'admin' })}
                    >
                      <Activity size={14} />
                      {t('jobActivityHub.open_hub_short', { ns: 'admin', defaultValue: 'Hub' })}
                    </Link>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default JobTable;
